import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { 
  User, Book, Clock, Calendar, Settings, Mail, Phone, 
  GraduationCap, MapPin, Activity, BookOpen, BarChart2 
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Profile = () => {
  const { isLoaded, user } = useUser();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingPhoto, setIsEditingPhoto] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const handleUpdateUser = async (updates) => {
    setIsUpdating(true);
    setUpdateError(null);
    try {
      await user.update(updates);
    } catch (error) {
      console.error('Error updating user:', error);
      setUpdateError('Failed to update profile. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUpdating(true);
    setUpdateError(null);
    try {
      await user.setProfileImage({ file });
    } catch (error) {
      console.error('Error uploading image:', error);
      setUpdateError('Failed to upload image. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const SettingsField = ({ field }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(field.value);
    const fileInputRef = useRef(null);

    const handleSave = async () => {
      if (field.type === 'image') return;

      const updates = {};
      switch (field.id) {
        case 'firstName':
          updates.firstName = value;
          break;
        case 'lastName':
          updates.lastName = value;
          break;
        case 'phoneNumber':
          try {
            await user.createPhoneNumber({ phoneNumber: value });
          } catch (error) {
            console.error('Error updating phone number:', error);
            return;
          }
          break;
      }

      if (Object.keys(updates).length > 0) {
        await handleUpdateUser(updates);
      }
      setIsEditing(false);
    };

    const inputClasses = `
      w-full px-3 py-2 rounded-lg border border-theme
      bg-secondary text-theme focus:ring-2 focus:ring-primary
      transition-colors
    `;

    if (field.type === 'image') {
      return (
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-theme">
              {field.label}
            </label>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-accent hover:text-theme"
            >
              Change Photo
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          <img
            src={user?.imageUrl || `https://ui-avatars.com/api/?name=${user?.firstName}+${user?.lastName}`}
            alt="Profile"
            className="w-24 h-24 rounded-full object-cover"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-2">
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium text-theme">
            {field.label}
          </label>
          {field.editable && (
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              className="text-accent hover:text-theme"
              disabled={isUpdating}
            >
              {isEditing ? 'Save' : 'Edit'}
            </button>
          )}
        </div>
        
        {field.type === 'select' ? (
          <select 
            disabled={!isEditing || isUpdating}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={inputClasses}
          >
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={field.type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!isEditing || isUpdating || !field.editable}
            className={inputClasses}
          />
        )}
      </div>
    );
  };

  if (!isLoaded || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sand"></div>
      </div>
    );
  }

  const settingsSections = [
    {
      id: 'profile',
      title: 'Profile Information',
      icon: <User size={20} className="text-theme" />,
      fields: [
        {
          id: 'profileImage',
          label: 'Profile Picture',
          type: 'image',
          value: user?.imageUrl,
          editable: true,
        },
        {
          id: 'firstName',
          label: 'First Name',
          type: 'text',
          value: user?.firstName,
          editable: true,
        },
        {
          id: 'lastName',
          label: 'Last Name',
          type: 'text',
          value: user?.lastName,
          editable: true,
        },
      ]
    },
    {
      id: 'contact',
      title: 'Contact Information',
      icon: <Mail size={20} className="text-theme" />,
      fields: [
        {
          id: 'email',
          label: 'Email Address',
          type: 'email',
          value: user?.primaryEmailAddress?.emailAddress,
          editable: false,
        },
        {
          id: 'phoneNumber',
          label: 'Phone Number',
          type: 'tel',
          value: user?.phoneNumbers?.[0]?.phoneNumber || '',
          editable: true,
        }
      ]
    },
    {
      id: 'preferences',
      title: 'Preferences',
      icon: <Settings size={20} className="text-theme" />,
      fields: [
        {
          label: 'Time Zone',
          type: 'select',
          value: 'America/New_York',
          editable: true,
          options: [
            { value: 'America/New_York', label: 'Eastern Time (ET)' },
            { value: 'America/Chicago', label: 'Central Time (CT)' },
            // ... add more options
          ]
        },
        {
          label: 'Notification Preferences',
          type: 'multiselect',
          value: ['email', 'push'],
          editable: true,
          options: [
            { value: 'email', label: 'Email Notifications' },
            { value: 'push', label: 'Push Notifications' },
          ]
        }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Show any update errors */}
      {updateError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {updateError}
        </div>
      )}

      {/* Settings Sections */}
      {settingsSections.map((section) => (
        <div
          key={section.id}
          className="p-6 rounded-lg bg-theme border border-theme theme-transition"
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="p-2 rounded-lg bg-secondary text-theme">
              {section.icon}
            </div>
            <h2 className="text-lg font-semibold text-theme">
              {section.title}
            </h2>
          </div>

          <div className="space-y-4">
            {section.fields.map((field) => (
              <SettingsField
                key={field.label}
                field={field}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Profile; 